import React, {
    Context,
    ReactNode,
    useState,
    useCallback,
    useEffect,
    useRef,
} from 'react';
import { useApolloClient, useMutation, useQuery } from '@apollo/client';
import { uuid } from 'uuidv4';
import S3 from 'aws-sdk/clients/s3';
import axios, { AxiosResponse } from 'axios';
import HttpStatus from 'http-status-codes';
import _uniqBy from 'lodash/uniqBy';
import { useSelector } from '@xstate/react';
import _isEqual from 'lodash/isEqual';
import { State } from 'xstate';

import { constants as resolverConstants, queryStrings } from '@trqk/resolvers';
import {
    constants,
    getAddStatementError,
    getFileSizeLabel,
    getWebpackEnvVar,
    getEnvironment,
    isLogEventFailure,
    locale,
    generateCSVFileKeyForS3,
} from '@trqk/utils';

import {
    SomeStatementsFailed,
    UploadHasStarted,
    UploadsAreFinished,
} from '../../components/toastMessages';
import {
    useTokenModal,
    useLogData,
    useTasks,
    useToast,
    useStatementSelection,
    useEntities,
} from '..';
import {
    isOnManageStatementsRoute,
    getUploadStatementTaskEvents,
} from '../../utils';
import { TASK_GROUP_ID_STATEMENT_UPLOADS } from '../../constants';

const { statementViewIngestionFields } = resolverConstants;
const environment = getEnvironment();

const {
    GET_ACTIVE_LOG_POLLS,
    GET_SESSION_CURRENT_USER,
    GET_STATEMENTS_UI_STATE,
    GET_UNVIEWED_STATEMENTS,
    TASK_UPLOAD_TO_S3,
} = queryStrings;

const { INGESTION_PROGRESS_CHUNKS } = constants;

const updateStatusDelay = 1000;
const addStatementDelay = 1100;

const startingIngestionStatusName = 'STARTING_INGESTION';

const UPLOAD_STATEMENTS_HANDLER_KEY = 'uploadStatements';
const UPLOAD_STATEMENTS_FAILURES_HANDLER_KEY = 'uploadStatementsFailures';

interface UploadStatementsProviderProps {
    children: ReactNode;
    UploadStatementsContext: Context<UploadStatementsProviderValue>;
}

/**
 * Exposes an object with properties and methods for uploading statements. Statement upload consists
 * of three phases:
 * 1. Upload a CSV file of a PRO statement to S3
 * 2. Call the `addStatementToTrqkUser` mutation to begin the ingestion pipeline
 * 3. Poll for log events that indicate either failure or successful ingestion
 *
 * Several features of the app need to know about statement ingestions, including the
 * ManageStatements modal, the StatementSelector and the EntitySelector. This provider is rather
 * complex because it is the logical intersection of all of these features.
 *
 * @returns {UploadStatementsProviderValue}
 */
export const UploadStatementsProvider = ({
    children,
    UploadStatementsContext,
}: UploadStatementsProviderProps): JSX.Element => {
    const bucketName = getWebpackEnvVar('web', 'AWS_S3_PRO_STATEMENT_BUCKET');
    const bucketRegion = getWebpackEnvVar('web', 'AWS_REGION');
    const identityPoolId = getWebpackEnvVar('web', 'AWS_IDENTITY_POOL_ID');
    const trqkDirectoryUrl = getWebpackEnvVar('web', 'TRQK_DIRECTORY_URL');
    const { cache } = useApolloClient();
    const { sendTaskEvent, taskMachineService } = useTasks();
    const { activeEntities, sendEntitySelectionEvent } = useEntities();
    const { add: addToast } = useToast();
    const {
        statementSelectionProps: { addStatement },
    } = useStatementSelection();
    const currentUserQuery = useQuery(GET_SESSION_CURRENT_USER);
    const statementsUiStateQuery = useQuery(GET_STATEMENTS_UI_STATE);
    const showUploadHasStarted =
        statementsUiStateQuery?.data?.uiState?.statements
            ?.showUploadHasStarted ?? false;
    const userId = currentUserQuery?.data?.session?.currentUser?.id;
    const { getToken } = useTokenModal();

    const [ingestionSuccessHistory, setIngestionSuccessHistory] = useState<
        IngestionSuccess[]
    >([]);

    // If a statement was added to the user with the mutation `addStatementToTrqkUser`, the
    // filename and statementView will be saved in an object within ingestionFailureHistory that is
    // keyed by its statement id. Note that these are statements that *can* still succeed; we are
    // keeping a record of them so that we can use them to collect future LogEvents that represent
    // failed ingestions.
    const [ingestionFailureHistory, setIngestionFailureHistory] = useState<
        IngestionFailure[]
    >([]);

    useEffect(() => {
        if (userId) {
            // Whenever the userId changes, reset the history data - this prevents data from
            // leaking between users.
            setIngestionSuccessHistory([]);
            setIngestionFailureHistory([]);
        }
    }, [userId]);

    /**
     * Phase 2 in the ingestion process: use the S3 location of a PRO statement to add the statement
     * to the user. This begins the backend ingestion pipeline. Further status updates / failures
     * will be detected from log events.
     */
    const addStatementToTrqkUser = useCallback(
        ({
            taskId,
            statementUrl,
            filename,
            fileSizeBytes,
        }: UploadStatementToTrqkUserInput): void => {
            sendTaskEvent({
                type: 'STATUS',
                value: {
                    taskId,
                    status: startingIngestionStatusName,
                    output: {
                        statementUrl,
                        statusMessage: 'Starting ingestion',
                    },
                },
            });

            getToken((token: string): void => {
                axios({
                    method: 'post',
                    url: trqkDirectoryUrl,
                    headers: {
                        Authorization: token,
                    },
                    // throw an error for non-200 status
                    validateStatus: (status: number): boolean =>
                        status === HttpStatus.OK,
                    data: {
                        query: `
                        mutation addStatementToTrqkUser(
                            $addstatementtotrqkuserinput: AddStatementToTrqkUserInput!
                            ) {
                                addStatementToTrqkUser(input: $addstatementtotrqkuserinput) {
                                    ${statementViewIngestionFields}
                                }
                            }
                        `,
                        variables: {
                            addstatementtotrqkuserinput: {
                                id: userId,
                                statement: statementUrl,
                            },
                        },
                    },
                })
                    .then(
                        (
                            response: AxiosResponse<{
                                data: {
                                    addStatementToTrqkUser: TrqkStatementView;
                                };
                                errors?: Error[];
                            }>,
                        ): void => {
                            const errors: Error[] =
                                response?.data?.errors ?? [];
                            const statementView =
                                response?.data?.data?.addStatementToTrqkUser ??
                                null;
                            const statementId =
                                statementView?.statement_id ?? null;

                            if (statementId === null) {
                                errors.push(
                                    new Error(
                                        locale.enUS.INGESTION.ERRORS.ADD_STATEMENT_ERROR,
                                    ),
                                );
                            }

                            if (errors.length > 1) {
                                throw getAddStatementError(errors);
                            }

                            sendTaskEvent({
                                type: 'STATUS',
                                value: {
                                    taskId,
                                    status: startingIngestionStatusName,
                                    output: {
                                        statementUrl,
                                        statementView,
                                    },
                                },
                            });

                            setIngestionFailureHistory(
                                (
                                    prev: IngestionFailure[],
                                ): IngestionFailure[] => [
                                    ...prev,
                                    {
                                        taskId,
                                        addStatementFailure: null,
                                        filename,
                                        fileSizeBytes,
                                        logEventFailures: [],
                                        statementView,
                                        statementUrl,
                                    },
                                ],
                            );
                        },
                    )
                    .catch((error: Error): void => {
                        sendTaskEvent({
                            type: 'ERROR',
                            value: {
                                taskId,
                                error: error.message,
                            },
                        });

                        setIngestionFailureHistory(
                            (prev: IngestionFailure[]): IngestionFailure[] => [
                                ...prev,
                                {
                                    taskId,
                                    addStatementFailure: {
                                        addStatementResult: null,
                                        addStatementSuccess: false,
                                        error: error.message,
                                        filename,
                                        statementUrl,
                                        uploadCanceled: false,
                                        uploadSuccess: true,
                                        userId,
                                    },
                                    filename,
                                    fileSizeBytes,
                                    statementView: null,
                                    statementUrl,
                                    logEventFailures: [],
                                },
                            ],
                        );
                    });
            });
        },
        [userId, sendTaskEvent, getToken],
    );

    // addStatementQueue contains a list of objects which represent an addStatementToTrqkUser
    // mutation to be triggered. They are queued so that they only occur at the addStatementDelay
    // frequency. This ensures that statements will get unique IDs, since each statement ID contains
    // a timestamp that is accurate to one second.
    const [addStatementQueue, setAddStatementQueue] = useState<
        UploadToS3TaskOutput[]
    >([]);

    const addStatementMutationsLeft = addStatementQueue.length;

    // While there are items in the addStatementQueue, remove the first item and use it to call
    // addStatementToTrqkUser. Do this on the addStatementDelay frequency.
    useEffect(() => {
        if (addStatementMutationsLeft > 0) {
            const intervalId = setInterval(() => {
                const nextAddStatementQueue = [...addStatementQueue];
                const currentItem = nextAddStatementQueue.shift();

                if (currentItem) {
                    const { taskId, filename, fileSizeBytes, location } =
                        currentItem;

                    if (location && filename) {
                        addStatementToTrqkUser({
                            taskId,
                            statementUrl: location,
                            filename,
                            fileSizeBytes: fileSizeBytes ?? 0,
                        });
                    }

                    setAddStatementQueue(nextAddStatementQueue);
                }
            }, addStatementDelay);

            return (): void => {
                clearInterval(intervalId);
            };
        }

        return (): void => undefined;
    }, [addStatementMutationsLeft, addStatementQueue, addStatementToTrqkUser]);

    /**
     * Phase 1 in the ingestion process: upload the CSV statement to S3.
     */
    const [taskUploadToS3] = useMutation<{
        taskUploadToS3: UploadToS3TaskOutput;
    }>(TASK_UPLOAD_TO_S3, {
        onCompleted: data => {
            const { taskUploadToS3: result } = data;
            const { completed } = result;

            if (completed) {
                // If upload was successful, add it to the queue of `addStatementToTrqkUser`
                // mutations to be triggered
                setAddStatementQueue(
                    (prev: UploadToS3TaskOutput[]): UploadToS3TaskOutput[] => [
                        ...prev,
                        result,
                    ],
                );
            }
        },
        onError: (): void => {
            // We don't mind if there is an error here, since it will trigger the appropriate
            // task event, but we do need to declare an onError method so Apollo Client doesn't
            // treat the error as unhandled.
        },
    });

    // updateQueues contains QueuedUpdates, each of which represents a log event that need to
    // trigger a task update. They are queued so that they only occur at the updateStatusDelay
    // frequency. This ensures that the user can actually read each update as it happens, which
    // helps smooth out the UX. Otherwise, a series of updates might all be fetched on the same
    // log poll and intermediate updates would be missed.
    const [updateQueues, setUpdateQueues] = useState<
        Record<string, QueuedUpdate[]>
    >({});

    const updatesLeft = Object.values(updateQueues).reduce(
        (acc: number, queue: QueuedUpdate[]): number => acc + queue.length,
        0,
    );

    // While there are updates in any of the arrays in updateQueues, remove the first update from
    // each array and call it. Do this on the updateStatusDelay frequency.
    useEffect(() => {
        if (updatesLeft > 0) {
            const intervalId = setInterval(() => {
                const queues = Object.entries(updateQueues);
                const nextUpdateQueues: Record<string, QueuedUpdate[]> = {};

                queues.forEach(([key, queue]): void => {
                    const queuedUpdate = queue.shift();

                    if (queuedUpdate) {
                        queuedUpdate.update(queuedUpdate.event);
                    }

                    nextUpdateQueues[key] = queue;
                });

                setUpdateQueues(nextUpdateQueues);
            }, updateStatusDelay);

            return (): void => {
                clearInterval(intervalId);
            };
        }

        return (): void => undefined;
    }, [updatesLeft, updateQueues]);

    const [entityRefreshState, setEntityRefreshState] =
        useState<EntityRefreshState>({});
    const { addHandler } = useLogData();

    /**
     * For ingestion failures, find the relevant IngestionFailure in ingestionFailureHistory and
     * add the event to the list of failures. There can be more than one failure per ingestion.
     */
    const addIngestionFailureToHistory = useCallback(
        (event: LogEvent): void => {
            const statementId = event?.detail?.statement_id ?? null;

            if (isLogEventFailure(event)) {
                const taskEvents = getUploadStatementTaskEvents(event);

                // Don't put the ERROR task event in the queue; just fire it immediately
                if (taskEvents.length > 0) {
                    taskEvents.forEach((taskEvent: TaskEvent): void => {
                        sendTaskEvent(taskEvent);
                    });
                }

                setIngestionFailureHistory(
                    (prev: IngestionFailure[]): IngestionFailure[] =>
                        prev.map(
                            ({
                                taskId,
                                addStatementFailure,
                                filename,
                                fileSizeBytes,
                                statementView,
                                statementUrl,
                                logEventFailures,
                            }: IngestionFailure): IngestionFailure => {
                                // Find the matching item in ingestionFailureHistory based on the
                                // statement_id from the event and add the event to its
                                // logEventFailures list
                                if (
                                    statementView?.statement_id === statementId
                                ) {
                                    return {
                                        taskId,
                                        addStatementFailure,
                                        filename,
                                        fileSizeBytes,
                                        statementView,
                                        statementUrl,
                                        logEventFailures: _uniqBy<LogEvent>(
                                            [...logEventFailures, event],
                                            'key',
                                        ),
                                    };
                                }

                                return {
                                    taskId,
                                    addStatementFailure,
                                    filename,
                                    fileSizeBytes,
                                    statementView,
                                    statementUrl,
                                    logEventFailures,
                                };
                            },
                        ),
                );
            }
        },
        [sendTaskEvent],
    );

    /**
     * Add new statementId to Apollo client cache within uiState.statements.unviewedStatements so
     * that the ManageStatements modal can indicate which statements have not been viewed.
     */
    const addUnviewedStatement = useCallback(
        (statementId: string): void => {
            const queryData = cache.readQuery({
                query: GET_UNVIEWED_STATEMENTS,
            });

            // I'm ignoring a TSC error here that is caused by the cache not being typed. It might
            // take a lot of work to type the cache at this point.
            const prev: string[] =
                // eslint-disable-next-line
                // @ts-ignore
                queryData?.uiState?.statements?.unviewedStatements ?? [];

            cache.writeQuery({
                query: GET_UNVIEWED_STATEMENTS,
                data: {
                    uiState: {
                        __typename: 'UIState',
                        statements: {
                            __typename: 'Statements',
                            unviewedStatements: [...prev, statementId],
                        },
                    },
                },
            });
        },
        [cache],
    );

    /**
     * When a new statement is ingested, add it to the entity selection state. This will ensure
     * that the activeEntities provided by useEntities contains the new statement.
     */
    const handleNewStatement = useCallback(
        (newStatementView: TrqkStatementView, entityId: string): void => {
            const newStatementId = newStatementView?.statement_id ?? null;

            if (newStatementId) {
                const activeEntity = activeEntities.find(
                    ({ id }: ActiveEntity): boolean => id === entityId,
                );

                if (activeEntity) {
                    sendEntitySelectionEvent({
                        type: 'ADD_STATEMENT_TO_ENTITIES',
                        value: {
                            entity: activeEntity.entity,
                            entityId,
                            statementId: newStatementId,
                            statementView: newStatementView,
                        },
                    });
                } else if (userId) {
                    getToken((token: string): void => {
                        axios({
                            method: 'post',
                            url: trqkDirectoryUrl,
                            headers: {
                                Authorization: token,
                            },
                            data: {
                                query: `
                                    query getTrqkEntityForNewStatement($id: ID!, $user_id: ID!) {
                                        getTrqkEntity(id: $id, user_id: $user_id) {
                                            id
                                            name
                                            pro
                                            role_type
                                        }
                                    }
                                `,
                                variables: {
                                    id: entityId,
                                    user_id: userId,
                                },
                            },
                        })
                            .then(
                                (
                                    response: AxiosResponse<{
                                        data: { getTrqkEntity: TrqkEntity };
                                    }>,
                                ): void => {
                                    const newEntity =
                                        response?.data?.data?.getTrqkEntity ??
                                        null;

                                    if (newEntity) {
                                        sendEntitySelectionEvent({
                                            type: 'ADD_STATEMENT_TO_ENTITIES',
                                            value: {
                                                entity: newEntity,
                                                entityId,
                                                statementId: newStatementId,
                                                statementView: newStatementView,
                                            },
                                        });
                                    } else if (
                                        ['development', 'staging'].includes(
                                            environment,
                                        )
                                    ) {
                                        // this could be the cause of the "houdini" effect in
                                        // ManageStatements
                                        // eslint-disable-next-line no-console
                                        console.log(
                                            'missing entity in getTrqkEntity.\nResponse:\n',
                                            JSON.stringify(response, null, 4),
                                        );
                                    }
                                },
                            )
                            .catch((error: Error): void => {
                                if (
                                    ['development', 'staging'].includes(
                                        environment,
                                    )
                                ) {
                                    // this could be the cause of the "houdini" effect in
                                    // ManageStatements
                                    // eslint-disable-next-line no-console
                                    console.log(
                                        'Error in getTrqkEntity: \n',
                                        JSON.stringify(error, null, 4),
                                    );
                                }
                            });
                    });
                }
            }
        },
        [sendEntitySelectionEvent, activeEntities, getToken, userId],
    );

    /**
     * Respond to the dashboardBuilt event. A dashboardBuilt event with an eventStatus of `success`
     * indicates a successful ingestion.
     */
    const handleDashboardBuilt = useCallback(
        (event: LogEvent): void => {
            setUpdateQueues(
                (
                    prevState: Record<string, QueuedUpdate[]>,
                ): Record<string, QueuedUpdate[]> => {
                    const nextState = { ...prevState };
                    const statement_id = event?.detail?.statement_id ?? null;
                    const entityId = event?.detail?.entity_id ?? null;
                    const { eventType, eventStatus } = event;

                    if (statement_id !== null) {
                        const queue: QueuedUpdate[] =
                            prevState[statement_id] ?? [];

                        const taskEvents = getUploadStatementTaskEvents(event);

                        taskEvents.forEach((taskEvent: TaskEvent) => {
                            queue.push({
                                event,
                                update: (updateEvent: LogEvent): void => {
                                    sendTaskEvent(taskEvent);

                                    if (taskEvent.type === 'SUCCESS') {
                                        // I'd rather not spend time figuring out how to type check
                                        // task outputs; they are widely used and a simple null
                                        // check is protecting against a missing value.
                                        /* eslint-disable */
                                        const statementId =
                                            // prettier-ignore
                                            taskEvent.value?.output
                                                    // @ts-ignore
                                                    ?.statement_id ?? null;
                                        /* eslint-enable */

                                        // If the statement was successfully ingested,
                                        if (
                                            typeof statementId === 'string' &&
                                            entityId !== null
                                        ) {
                                            addUnviewedStatement(statementId);

                                            // Add the new statement to the statement selection
                                            // machine
                                            addStatement({
                                                statementId,
                                                entityId,
                                                handleNewStatement,
                                            });

                                            // Add successful ingestion to ingestionSuccessHistory
                                            setIngestionSuccessHistory(
                                                (
                                                    prev: IngestionSuccess[],
                                                ): IngestionSuccess[] => [
                                                    ...prev,
                                                    {
                                                        statementId,
                                                        uploadedAt:
                                                            updateEvent.dateData,
                                                    },
                                                ],
                                            );
                                        }
                                    }
                                },
                            });
                        });

                        nextState[statement_id] = queue;
                    }

                    // When a dashboardBuilt.success event happens, set the corresponding
                    // entityRefreshState value so that the Tracks table can detect that the tracks
                    // list is being rebuilt due to a statement being ingested
                    if (
                        entityId !== null &&
                        eventType === 'dashboardBuilt' &&
                        eventStatus === 'success'
                    ) {
                        setEntityRefreshState(
                            (prev: EntityRefreshState): EntityRefreshState => {
                                const prevEntityStatus = prev[entityId] ?? null;

                                let nextEntityStatus: EntityRefreshStatus = {
                                    entityId,
                                    isIngestingStatement: true,
                                    isDeletingStatement: false,
                                    refreshedAt: 0,
                                    tracksFetchedAt: 0,
                                };

                                if (prevEntityStatus !== null) {
                                    // preserve prior values
                                    nextEntityStatus = {
                                        ...prevEntityStatus,
                                        isIngestingStatement: true,
                                    };
                                }

                                return {
                                    ...prev,
                                    [entityId]: nextEntityStatus,
                                };
                            },
                        );
                    }

                    return nextState;
                },
            );
        },
        [addStatement, addUnviewedStatement, handleNewStatement, sendTaskEvent],
    );

    /**
     * When a entityRefreshed.success event happens, respond accordingly. These events are kept
     * in state so that the TracksTable can display a message about the tracks list being recently
     * updated.
     */
    const handleEntityRefreshed = useCallback((event: LogEvent): void => {
        const entityId = event?.detail?.entity_id ?? null;
        const status = event.eventStatus;

        if (entityId !== null && status === 'success') {
            // set the corresponding entityRefreshState value accordingly
            setEntityRefreshState(
                (prev: EntityRefreshState): EntityRefreshState => {
                    const prevEntityStatus = prev[entityId] ?? null;

                    let nextEntityStatus: EntityRefreshStatus = {
                        entityId,
                        isIngestingStatement: false,
                        isDeletingStatement: false,
                        refreshedAt: Date.now(),
                        tracksFetchedAt: 0,
                    };

                    if (prevEntityStatus !== null) {
                        // preserve prior values
                        nextEntityStatus = {
                            ...prevEntityStatus,
                            isIngestingStatement: false,
                            refreshedAt: Date.now(),
                        };
                    }

                    return {
                        ...prev,
                        [entityId]: nextEntityStatus,
                    };
                },
            );
        }
    }, []);

    // Add event handlers for the log events that relate to statement uploads.
    useEffect(() => {
        addHandler({
            eventTypes: ['dashboardBuilt'],
            handler: handleDashboardBuilt,
            handlerKey: UPLOAD_STATEMENTS_HANDLER_KEY,
        });

        addHandler({
            eventTypes: ['dashboardBuilt'],
            handler: addIngestionFailureToHistory,
            handlerKey: UPLOAD_STATEMENTS_FAILURES_HANDLER_KEY,
        });

        addHandler({
            eventTypes: ['entityRefreshed'],
            handler: handleEntityRefreshed,
            handlerKey: UPLOAD_STATEMENTS_HANDLER_KEY,
        });

        addHandler({
            eventTypes: ['entityValidation'],
            handler: addIngestionFailureToHistory,
            handlerKey: UPLOAD_STATEMENTS_FAILURES_HANDLER_KEY,
        });
    }, [
        addIngestionFailureToHistory,
        handleDashboardBuilt,
        handleEntityRefreshed,
    ]);

    // Each upload results in a ManagedUpload instance that we will need access to in order to
    // allow the user to cancel the upload. We use a ref to maintain access to a single object
    // that contains each of the ManagedUpload instances.
    const managedUploads = useRef({} as ManagedUploadsRef);

    // onTaskCancel is bound to the 'CANCEL' event when the task is started. This allows us to
    // call: `sendTaskEvent({ type: 'CANCEL', value: { taskId }})` within <ManageStatementsUploads>
    // in order to:
    //  1) notify the task machine to cancel the task,
    //  2) cancel the upload with managedUpload.abort(), and:
    //  3) cancel polling for the statement view to have a final status_name (COMPLETE, ERROR or
    //     FAILED)
    const onTaskCancel = (task: UploadStatementTask): void => {
        const managedUpload = managedUploads?.current?.[task.taskId];

        if (managedUpload) {
            managedUpload.abort();
            delete managedUploads.current[task.taskId];
        }
    };

    const handleFiles = (files: File[]): void => {
        const taskCreators = files.map(
            (file: File): TaskCreator<FilenameInput> => {
                const taskId = uuid();
                const fileSizeLabel = getFileSizeLabel(file.size);

                return {
                    taskId,
                    input: {
                        filename: file.name,
                    },
                    status: 'UPLOADING',
                    onTaskStart: (bindOnTaskCancel: BindOnTaskCancel): void => {
                        sendTaskEvent({
                            type: 'STATUS',
                            value: {
                                taskId,
                                status: 'UPLOADING',
                                output: {
                                    statusMessage: `Uploading ${fileSizeLabel} statement`,
                                },
                            },
                        });

                        bindOnTaskCancel(onTaskCancel);

                        taskUploadToS3({
                            context: {
                                sendTaskEvent,
                                bindManagedUpload: (
                                    managedUpload: S3.ManagedUpload,
                                ): void => {
                                    if (managedUploads?.current) {
                                        managedUploads.current[taskId] =
                                            managedUpload;
                                    }
                                },
                                generateS3Key: (currentFile: File): string =>
                                    generateCSVFileKeyForS3({
                                        baseNameFallback: 'statement',
                                        file: currentFile,
                                        userId: userId ?? 'unknown_user',
                                    }),
                            },
                            variables: {
                                bucketName,
                                bucketRegion,
                                doTaskUpdates: {
                                    error: false,
                                    progress: true,
                                    success: false,
                                },
                                file,
                                identityPoolId,
                                progressShare: INGESTION_PROGRESS_CHUNKS.upload,
                                taskId,
                            },
                        });
                    },
                };
            },
        );

        sendTaskEvent({
            type: 'CREATE_GROUP',
            value: {
                groupId: TASK_GROUP_ID_STATEMENT_UPLOADS,
                taskCreators,
                onGroupStart: (): void => {
                    if (showUploadHasStarted) {
                        addToast({
                            id: 'statement-upload-has-started',
                            kind: 'info-card',
                            content: <UploadHasStarted />,
                        });
                    }
                },
                onGroupStopped: (
                    input: OnGroupStoppedInput<
                        Record<string, unknown>,
                        Record<string, unknown>
                    >,
                ): void => {
                    const { error, success } = input;
                    const isOnManageStatements = isOnManageStatementsRoute(
                        window.location.pathname,
                    );

                    if (!isOnManageStatements) {
                        if (error.length === 0) {
                            addToast({
                                id: 'statement-uploads-are-finished',
                                kind: 'info-card',
                                RenderContent: UploadsAreFinished,
                            });
                        } else {
                            addToast({
                                id: 'statement-uploads-failed',
                                kind: 'warning-card',
                                RenderContent: SomeStatementsFailed,
                                renderContentInput: {
                                    errorTasks: error,
                                    successTasks: success,
                                },
                            });
                        }
                    }
                },
            },
        });
    };

    const taskGroupSelector = (
        state: State<TaskContext, TaskEvent, TaskStateSchema>,
    ): UploadStatementTask[] => {
        if (state?.context?.tasks) {
            return Array.from(state.context.tasks.values()).filter(
                (task: UnknownTask): boolean =>
                    task.groupId === TASK_GROUP_ID_STATEMENT_UPLOADS &&
                    !task.canceled, // canceled tasks will still exist until the entire task
                // group stops
            );
        }

        return [];
    };

    const compareUploadTasks = (
        prevTasks: UploadStatementTask[],
        nextTasks: UploadStatementTask[],
    ): boolean => _isEqual(prevTasks, nextTasks);

    const uploadTasks = useSelector(
        taskMachineService,
        taskGroupSelector,
        compareUploadTasks,
    );

    const hasActiveUploads = uploadTasks.reduce(
        (acc: boolean, task: UploadStatementTask): boolean => {
            const canceled = task?.canceled ?? false;
            const done = task?.done ?? false;
            const error = task?.error ?? null;

            if (!canceled && !done && error === null) {
                return true;
            }

            return acc;
        },
        false,
    );

    useEffect(() => {
        // Update the cache so that LogDataProvider uses the short log polling interval
        if (hasActiveUploads) {
            cache.writeQuery({
                query: GET_ACTIVE_LOG_POLLS,
                data: {
                    uiState: {
                        __typename: 'UIState',
                        activeLogPolls: {
                            __typename: 'ActiveLogPolls',
                            uploadStatements: true,
                        },
                    },
                },
            });
        } else {
            // Update the cache so that LogDataProvider uses the long polling interval if there
            // are no other active polls
            cache.writeQuery({
                query: GET_ACTIVE_LOG_POLLS,
                data: {
                    uiState: {
                        __typename: 'UIState',
                        activeLogPolls: {
                            __typename: 'ActiveLogPolls',
                            uploadStatements: false,
                        },
                    },
                },
            });
        }
    }, [hasActiveUploads, cache]);

    // ingestionFailureHistory includes details for all uploads, so we must filter it to find items
    // that actually represent failed ingestions
    const failedIngestions = ingestionFailureHistory.filter(
        (ingestionFailure: IngestionFailure): boolean => {
            if (
                ingestionFailure.logEventFailures.length < 1 &&
                ingestionFailure.addStatementFailure === null
            ) {
                return false;
            }

            return true;
        },
    );

    return (
        <UploadStatementsContext.Provider
            value={{
                entityRefreshState,
                failedIngestions,
                handleFiles,
                hasActiveUploads,
                ingestionFailureHistory,
                ingestionSuccessHistory,
                setEntityRefreshState,
                setIngestionFailureHistory,
                uploadTasks,
            }}
        >
            {children}
        </UploadStatementsContext.Provider>
    );
};
